import Button from '@components/Button'
import { LineChart } from '@components/LineChart'
import Touchable from '@components/Touchable'
import useTokenPriceInfo, { Range } from '@plug-core/hooks/useTokenPriceInfo'
import { type PriceQuote, TimeFrame } from '@plug-core/interfaces/tokenPriceInfo'
import {
  Avatar,
  Box,
  CircularProgress,
  Icon,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { ROUTES } from '@utils/routes'
import { differenceInDays, format } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useLocales, useUserTokens } from '@plug-core/state'
import { TransactionListItem } from '@components/TransactionList/TransactionListItem'
import { HiArrowsUpDown } from 'react-icons/hi2'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { HiMiniStar } from 'react-icons/hi2'
import { IoIosSend } from 'react-icons/io'
import { HiArrowUpRight } from 'react-icons/hi2'
import LedgerScannerClient, { TransactionKind } from '@plug-core/clients//LedgerScannerClient'
import useWallet from '@plug-core/hooks/useWallet'
import useToken from '@plug-core/hooks/useToken'
import useBalances from '@plug-core/hooks/useBalances'
import { logger } from '@plug-core/logger'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { TransactionActivity } from '@plug-core/interfaces/transactionActivity'
import {
  BTC_ID,
  CKBTC_ICP_ID,
  ETH_ID,
  ICP_ICP_ID,
  ODINBTC_ID,
  USDC_ARB_ID,
  USDC_ETH_ID,
  USDC_ICP_ID,
} from '@plug-core/constants/tokenIds'

const RANGES: Range[] = [
  { value: 60, label: '1H', frame: TimeFrame.FIVE_MINUTES },
  { value: 24 * 60, label: '1D', frame: TimeFrame.ONE_HOUR },
  { value: 7 * 24 * 60, label: '1W', frame: TimeFrame.ONE_HOUR },
  { value: 30 * 24 * 60, label: '1M', frame: TimeFrame.ONE_DAY },
]

const TokenPriceChart = ({
  priceQuotes,
  onMouseMove,
  isLoading,
}: {
  priceQuotes: PriceQuote[]
  onMouseMove: (point?: PriceQuote) => void
  isLoading: boolean
}) => {
  const { t } = useTranslation()

  if (isLoading) {
    return <CircularProgress thickness={5} />
  } else if (priceQuotes?.length && priceQuotes.some((pq) => pq.price)) {
    return (
      <LineChart
        data={priceQuotes}
        width={330}
        height={200}
        paddingY={40}
        onMouseMove={onMouseMove}
      />
    )
  } else {
    return (
      <>
        <Icon sx={{ pb: 1 }}>😔</Icon>
        <Typography>{t('tokens.unavailableAnalytics')}</Typography>
      </>
    )
  }
}

export const TokenDetails = () => {
  const [chosenRange, setChosenRange] = useState<Range>(
    RANGES.find((r) => r.label === '1W') || RANGES[0]
  )
  const [selectedPrice, setSelectedPrice] = useState<PriceQuote>()
  const [transactions, setTransactions] = useState<TransactionActivity[]>([])

  const { savedTokens, toggleToken } = useUserTokens()
  const { palette } = useTheme()
  const { wallet } = useWallet()
  const { t } = useTranslation()
  const { userLocale } = useLocales()

  const navigate = useNavigate()

  const { tokenId } = useParams<{ tokenId: string }>()
  const token = useToken(tokenId)

  const {
    priceQuotes: normalizedPriceQuotes,
    price,
    isLoading,
  } = useTokenPriceInfo({
    tokenId,
    range: chosenRange,
  })

  const priceQuotes = normalizedPriceQuotes.map((pq) => ({
    ...pq,
    price: pq.price * userLocale.currency.usdExchangeRate,
  }))

  const { findBalance } = useBalances()
  const balance = findBalance(token)?.balance || 0

  useEffect(() => {
    if (!token.symbol || token.id === ODINBTC_ID) return

    LedgerScannerClient.fetchTransactions(wallet, {
      types: [TransactionKind.Transfer],
      tokenSymbol: token.symbol,
      pageSize: 5,
    })
      .then(setTransactions)
      .catch(logger.debug)
  }, [wallet, token.symbol])

  const getPriceChange = useCallback((currentPrice: PriceQuote, firstPoint: PriceQuote) => {
    return ((currentPrice.price - firstPoint.price) / firstPoint.price) * 100
  }, [])

  const priceChange = useMemo(() => {
    if (priceQuotes.length < 2) return undefined

    const firstPoint = priceQuotes[0]

    if (selectedPrice) {
      return getPriceChange(selectedPrice, firstPoint)
    }

    return getPriceChange(priceQuotes[priceQuotes.length - 1], firstPoint)
  }, [selectedPrice, getPriceChange, priceQuotes])

  const priceChangeFormatted = useMemo(() => {
    if (!priceChange) return

    return `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)} %`
  }, [priceChange])

  const relativeDate = useMemo(() => {
    if (!selectedPrice) return t('tokenDetails.today')

    const dayDiff = differenceInDays(new Date(), new Date(selectedPrice.timestamp))

    if (dayDiff === 0) return t('tokenDetails.today')
    if (dayDiff === 1) return t('tokenDetails.yesterday')

    return format(new Date(selectedPrice.timestamp), 'd MMM yyyy')
  }, [selectedPrice])

  const isFavorite = useMemo(() => {
    return (savedTokens[wallet.principalId] || []).includes(tokenId || '')
  }, [savedTokens, wallet.principalId, tokenId])

  const renderTokenDetails = () => {
    return (
      <Paper sx={{ height: 72 }} elevation={0} component={ListItem}>
        <ListItemAvatar>
          <Avatar src={token.logo} />
        </ListItemAvatar>
        <ListItemText
          primary={formatMoney(price, undefined, { format: 'precise' })}
          secondary={token.symbol}
        />
        {typeof priceChange !== 'undefined' && (
          <ListItemText
            primaryTypographyProps={{
              textAlign: 'right',
              color: priceChange > 0 ? 'rainbow.green' : 'rainbow.red',
            }}
            secondaryTypographyProps={{
              textAlign: 'right',
              color: priceChange > 0 ? 'rainbow.green' : 'rainbow.red',
            }}
            primary={priceChangeFormatted}
            secondary={relativeDate}
          />
        )}
      </Paper>
    )
  }

  const renderBalance = () => {
    return (
      <Paper sx={{ height: 72 }} elevation={0} component={ListItem}>
        <ListItemText
          primary="Balance"
          secondary={formatNumber(balance, token.symbol?.toUpperCase())}
        />
        <ListItemText
          primaryTypographyProps={{
            textAlign: 'right',
          }}
          secondaryTypographyProps={{
            textAlign: 'right',
          }}
          primary="Value"
          secondary={typeof price === 'number' && formatMoney(balance * price)}
        />
      </Paper>
    )
  }

  const renderFavoriteIcon = () => {
    if (tokenId === ICP_ICP_ID) return

    return (
      <Stack justifyContent="center">
        <Touchable onClick={() => toggleToken(wallet.principalId, tokenId!)} scaleFactor={0.1}>
          <HiMiniStar color={isFavorite ? palette.rainbow.yellow : '#F3F4F6'} size={20} />
        </Touchable>
      </Stack>
    )
  }

  return (
    <Stack spacing={2} px={2} py={2}>
      <Titlebar
        onActionClick={{
          type: 'back',
          action: () => navigate(-1),
        }}
        leftItem={renderFavoriteIcon()}
      />
      <Stack spacing={2}>{renderTokenDetails()}</Stack>

      <Box
        height={220}
        justifyContent="center"
        alignItems="center"
        display="flex"
        flexDirection="column"
      >
        <TokenPriceChart
          isLoading={isLoading}
          priceQuotes={priceQuotes}
          onMouseMove={(point) =>
            setSelectedPrice(
              point
                ? {
                    timestamp: point.timestamp,
                    price: point.price,
                  }
                : undefined
            )
          }
        />
      </Box>

      <Stack direction="row" justifyContent="center" spacing={1}>
        {RANGES.map((range, index) => {
          const selected = range.value === chosenRange.value

          return (
            <Touchable key={index} onClick={() => setChosenRange(range)}>
              <Stack
                bgcolor={selected ? 'secondary.light' : 'background.paper'}
                py={0.5}
                px={1.2}
                borderRadius={5}
              >
                <Typography>{range.label}</Typography>
              </Stack>
            </Touchable>
          )
        })}
      </Stack>
      {renderBalance()}
      {token.chain === 'eth' && (
        <Button
          variant="contained"
          startIcon={<HiArrowUpRight />}
          onClick={() => navigate(ROUTES.MINT_CK(token?.id || ETH_ID))}
          fullWidth
        >
          Convert to ck{token.symbol}
        </Button>
      )}
      {token.id === BTC_ID && (
        <Button
          variant="contained"
          startIcon={<HiArrowUpRight />}
          onClick={() => navigate(ROUTES.MINT_CK(BTC_ID))}
          fullWidth
        >
          Convert to ck{token.symbol}
        </Button>
      )}
      {token.id === CKBTC_ICP_ID && (
        <Button
          variant="contained"
          startIcon={<HiArrowUpRight />}
          onClick={() => navigate(ROUTES.DEPOSIT_ODIN)}
          fullWidth
        >
          {t('tokenDetails.depositToOdin')}
        </Button>
      )}
      {token.id === USDC_ICP_ID && (
        <Button
          variant="contained"
          startIcon={<HiArrowUpRight />}
          onClick={() => navigate(ROUTES.BRIDGE)}
          fullWidth
        >
          {t('tokenDetails.bridgeToEVM')}
        </Button>
      )}
      {(token.id === USDC_ETH_ID || token.id === USDC_ARB_ID) && (
        <Button
          variant="contained"
          startIcon={<HiArrowUpRight />}
          onClick={() => navigate(ROUTES.BRIDGE)}
          fullWidth
        >
          {t('tokenDetails.bridgeToICP')}
        </Button>
      )}
      {token.id === ODINBTC_ID && (
        <Button
          variant="contained"
          startIcon={<HiArrowUpRight />}
          onClick={() => navigate(ROUTES.DEPOSIT_ODIN)}
          fullWidth
        >
          {t('tokenDetails.withdrawFromOdin')}
        </Button>
      )}
      <Stack spacing={1} direction="row">
        <Button
          variant="contained"
          startIcon={<HiArrowsUpDown />}
          onClick={() =>
            navigate(ROUTES.SWAP, {
              state: {
                fromTokenId: token.id,
                chain: token.chain,
              },
            })
          }
          fullWidth
        >
          Swap
        </Button>
        <Button
          variant="rainbow"
          onClick={() => navigate(ROUTES.SEND, { state: { selectedTokenId: tokenId } })}
          startIcon={<IoIosSend />}
          fullWidth
        >
          Send
        </Button>
      </Stack>

      {token.description && (
        <Stack spacing={0.5}>
          <Typography variant="h6">{t('tokenDetails.description')}</Typography>
          <Typography color="GrayText" variant="body2">
            {token.description}
          </Typography>
        </Stack>
      )}
      {transactions.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="h6">{t('tokenDetails.lastTransactions')}</Typography>
          <Stack component={List} spacing={1}>
            {transactions.map((transaction) => (
              <TransactionListItem key={transaction.transactionId} transaction={transaction} />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
